import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Share2 } from 'lucide-react';
import api, { errorMessage, toPage } from '../services/api';
import {
  PageHeader,
  Card,
  Badge,
  Segmented,
  SearchInput,
  Pagination,
  Button,
  ErrorBanner,
  SkeletonList,
  EmptyState,
  ResponsiveList,
  OrderStatusBadge,
  PlatformBadge,
  ORDER_PLATFORM_TABS,
  useDebounce
} from '../components/ui';
import { formatCurrency, formatNumber, formatDateTime, fromUnix } from '../utils/format';

const VIEW_TABS = [
  { value: 'sharers', label: 'Người chia sẻ' },
  { value: 'orders', label: 'Đơn từ link chia sẻ' }
];
const STATUS_TABS = [
  { value: '', label: 'Tất cả' },
  { value: 'pending', label: 'Chờ duyệt' },
  { value: 'approved', label: 'Đã duyệt' },
  { value: 'cancelled', label: 'Đã huỷ' }
];

const SummaryCard = ({ label, value }) => (
  <Card className="p-3.5">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="tabular mt-1 text-lg font-semibold text-gray-900">{value}</p>
  </Card>
);

const SharerCard = ({ row, onOpen }) => (
  <Card as="button" type="button" className="w-full p-4 text-left hover:border-gray-400" onClick={() => onOpen(row)}>
    <div className="flex items-start justify-between gap-2">
      <p className="min-w-0 truncate text-sm font-medium text-gray-900">{row.email || '—'}</p>
      {row.fraud_items > 0 && <Badge tone="red">Gian lận: {formatNumber(row.fraud_items)}</Badge>}
    </div>
    <p className="mt-1 text-xs text-gray-500">
      {formatNumber(row.links)} link · {formatNumber(row.orders)} đơn · {formatNumber(row.cancelled_orders)} đơn huỷ
    </p>
    <p className="mt-2 text-xs text-gray-600">
      Chờ duyệt <span className="font-semibold text-gray-900">{formatCurrency(row.pending_commission)}</span>
      {' · '}Đã duyệt <span className="font-semibold text-gray-900">{formatCurrency(row.approved_commission)}</span>
    </p>
  </Card>
);

const OrderCard = ({ row, onOpen }) => (
  <Card as="button" type="button" className="w-full p-4 text-left hover:border-gray-400" onClick={() => onOpen(row)}>
    <div className="flex items-start justify-between gap-2">
      <p className="min-w-0 truncate font-mono text-sm font-semibold text-gray-900">{row.order_sn}</p>
      <span className="flex-shrink-0 text-sm font-semibold text-gray-900">{formatCurrency(row.commission)}</span>
    </div>
    <p className="mt-0.5 truncate text-xs text-gray-600">{row.email || '—'}{row.shop_name ? ` · ${row.shop_name}` : ''}</p>
    <div className="mt-2 flex flex-wrap gap-1.5">
      <PlatformBadge platform={row.platform} />
      <OrderStatusBadge status={row.order_status} />
      <Badge tone={row.is_withdrawable ? 'green' : 'yellow'}>{row.is_withdrawable ? 'Đã duyệt' : 'Chưa duyệt'}</Badge>
      {row.fraud_items > 0 && <Badge tone="red">Gian lận: {formatNumber(row.fraud_items)}</Badge>}
    </div>
    <p className="mt-2 text-xs text-gray-400">
      <span className="font-mono">{row.subid}</span>{row.pending_date ? ` · ${formatDateTime(fromUnix(row.pending_date))}` : ''}
    </p>
  </Card>
);

const ShareCommissionsPage = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('sharers');
  const [platform, setPlatform] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search.trim());

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['share-commissions', view, platform, status, debouncedSearch, page],
    async () => {
      const params = { page, limit: 20 };
      if (platform) params.platform = platform;
      if (debouncedSearch) params.search = debouncedSearch;
      if (view === 'orders' && status) params.status = status;
      return toPage(await api.get(`/admin/share-commissions/${view}`, { params }));
    },
    { keepPreviousData: true }
  );

  const rows = data?.items || [];
  const pagination = data?.pagination || {};
  // summary chỉ có ở tab Người chia sẻ; giữ lại khi chuyển tab bằng query riêng
  const { data: overview } = useQuery(
    ['share-commissions-summary', platform, debouncedSearch],
    async () => {
      const params = { page: 1, limit: 1 };
      if (platform) params.platform = platform;
      if (debouncedSearch) params.search = debouncedSearch;
      return toPage(await api.get('/admin/share-commissions/sharers', { params })).summary;
    },
    { keepPreviousData: true }
  );

  const change = (setter) => (value) => { setter(value); setPage(1); };

  // Bấm người chia sẻ: xem các đơn của người đó (tìm theo user ID để không khớp nhầm email gần giống)
  const openSharer = (row) => {
    setSearch(row.user_id);
    setStatus('');
    setView('orders');
    setPage(1);
  };

  const sharerColumns = [
    {
      key: 'email',
      title: 'Người chia sẻ',
      render: (row) => (
        <span className="inline-flex items-center gap-2">
          <span className="font-medium text-gray-900">{row.email || '—'}</span>
          {row.fraud_items > 0 && <Badge tone="red">Gian lận: {formatNumber(row.fraud_items)}</Badge>}
        </span>
      )
    },
    { key: 'links', title: 'Link', align: 'right', render: (row) => formatNumber(row.links) },
    { key: 'orders', title: 'Đơn', align: 'right', render: (row) => formatNumber(row.orders) },
    { key: 'cancelled', title: 'Đơn huỷ', align: 'right', render: (row) => formatNumber(row.cancelled_orders) },
    { key: 'pending', title: 'Chờ duyệt', align: 'right', render: (row) => formatCurrency(row.pending_commission) },
    { key: 'approved', title: 'Đã duyệt', align: 'right', render: (row) => <span className="font-semibold text-gray-900">{formatCurrency(row.approved_commission)}</span> }
  ];

  const orderColumns = [
    { key: 'sn', title: 'Mã đơn', render: (row) => <span className="font-mono font-medium text-gray-900">{row.order_sn}</span> },
    {
      key: 'user',
      title: 'Người chia sẻ',
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.email || '—'}</p>
          {row.shop_name && <p className="text-xs text-gray-500">{row.shop_name}</p>}
        </div>
      )
    },
    { key: 'platform', title: 'Sàn', render: (row) => <PlatformBadge platform={row.platform} /> },
    {
      key: 'status',
      title: 'Trạng thái',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          <OrderStatusBadge status={row.order_status} />
          <Badge tone={row.is_withdrawable ? 'green' : 'yellow'}>{row.is_withdrawable ? 'Đã duyệt' : 'Chưa duyệt'}</Badge>
          {row.fraud_items > 0 && <Badge tone="red">Gian lận: {formatNumber(row.fraud_items)}</Badge>}
        </div>
      )
    },
    { key: 'subid', title: 'Sub ID', render: (row) => <span className="font-mono text-xs text-gray-500">{row.subid}</span> },
    { key: 'date', title: 'Đặt hàng', render: (row) => <span className="whitespace-nowrap text-xs text-gray-500">{row.pending_date ? formatDateTime(fromUnix(row.pending_date)) : '—'}</span> },
    { key: 'commission', title: 'Hoa hồng', align: 'right', render: (row) => <span className="font-semibold text-gray-900">{formatCurrency(row.commission)}</span> }
  ];

  return (
    <div>
      <PageHeader
        title="Chia sẻ hoa hồng"
        subtitle="Link user tạo bằng nút Chia sẻ nhận hoa hồng (subid bắt đầu bằng SH). Duyệt hoặc từ chối đơn ở trang chi tiết đơn."
      />

      <div className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
        <SummaryCard label="Người chia sẻ / link" value={`${formatNumber(overview?.sharers)} / ${formatNumber(overview?.links)}`} />
        <SummaryCard label="Đơn / đơn huỷ" value={`${formatNumber(overview?.orders)} / ${formatNumber(overview?.cancelled_orders)}`} />
        <SummaryCard label="Hoa hồng chờ duyệt" value={formatCurrency(overview?.pending_commission)} />
        <SummaryCard label="Hoa hồng đã duyệt" value={formatCurrency(overview?.approved_commission)} />
      </div>

      <div className="filter-bar">
        <Segmented value={view} onChange={change(setView)} options={VIEW_TABS} />
        <Segmented value={platform} onChange={change(setPlatform)} options={ORDER_PLATFORM_TABS} />
        {view === 'orders' && <Segmented value={status} onChange={change(setStatus)} options={STATUS_TABS} />}
        <div className="flex gap-2 lg:min-w-[18rem] lg:flex-1">
          <SearchInput className="flex-1" value={search} onChange={change(setSearch)} placeholder="Email hoặc user ID" />
          {search && <Button variant="outline" onClick={() => change(setSearch)('')}>Xoá lọc</Button>}
        </div>
      </div>

      <ErrorBanner message={error ? errorMessage(error, 'Không thể tải dữ liệu') : ''} onRetry={refetch} />

      {isLoading ? (
        <SkeletonList />
      ) : (
        <ResponsiveList
          items={rows}
          keyField={view === 'sharers' ? 'user_id' : 'id'}
          renderCard={(row) => (view === 'sharers'
            ? <SharerCard row={row} onOpen={openSharer} />
            : <OrderCard row={row} onOpen={(order) => navigate(`/orders/${order.id}`)} />)}
          columns={view === 'sharers' ? sharerColumns : orderColumns}
          onRowClick={view === 'sharers' ? openSharer : (order) => navigate(`/orders/${order.id}`)}
          empty={(
            <EmptyState
              icon={Share2}
              title={view === 'sharers' ? 'Chưa có người chia sẻ' : 'Chưa có đơn từ link chia sẻ'}
              description="Thử đổi sàn hoặc bộ lọc."
            />
          )}
        />
      )}

      {pagination.total > 0 && (
        <Pagination page={page} totalPages={pagination.pages} total={pagination.total} onChange={setPage} disabled={isFetching} />
      )}
    </div>
  );
};

export default ShareCommissionsPage;
